<template>
  <div class="py-[1.16rem] bg-pitch-black">
    <!-- Error Message -->
    <div v-if="error" class="text-red-500 h-[70vh] flex justify-center items-center">
      <p>An error occurred: {{ error }}</p>
    </div>

    <!-- Header and Actions -->
    <div v-else class="flex justify-between items-center pb-3 px-5 mt-[-5px] border-b-2 border-rose-light">
      <div class="flex-1 flex items-center justify-start">
        <button v-if="!isSearching" @click="openFileUpload" class="text-prime-golden text-2xl">
          <i class="fa fa-plus"></i>
        </button>

        <p v-if="!isSearching" class="text-gray-400 text-xs flex-1 text-left ml-4">
          Showing all files ({{ fileStore?.totalFiles }})
        </p>
        <div v-else class="mt-1 flex-1 min-w-64 p-0">
          <input
            v-model="searchQuery"
            @input="searchFiles"
            class="w-full bg-pitch-black outline-none rounded-md text-sm text-total-white"
            placeholder="Search files..."
          />
        </div>
      </div>

      <!-- Actions Section -->
      <div class="flex items-center space-x-4">
        
        <button
          v-if="!isSearching"
          @click="toggleSearch"
          class="text-prime-golden text-lg"
        >
          <i class="fa fa-search"></i>
        </button>
        
        <button v-else @click="toggleSearch" class="text-prime-golden">
          <div class="bg-gray-500 rounded-full w-4 h-4 flex items-center justify-center">
            <i class="fas fa-times text-xs text-black"></i>
          </div>
        </button>
        
      </div>
    </div>

    <!-- Files List -->
    <div class="cards-section-height overflow-y-auto pb-[100px]" v-if="!error && filteredFiles.length">
      <div class="h-3"></div>
      <FileCard
        v-for="file in filteredFiles"
        :key="file.id"
        :fileId="file.id"
        :filename="file.file_name"
        :size="file.file_size"
        :type="file.file_type"
        :uploader="file.uploader"
        :timestamp="file.date_time_uploaded"
        :fileUrl="file.file_url"
        :threadId="file.thread_id"
        @deleteFile="deleteFile"
        @goToMessage="goToThread"
      />
    </div>

    <!-- No Files Found Message -->
    <div v-else-if="!error">
      <p
        v-if="searchQuery"
        class="text-gray-400 h-[65vh] flex justify-center items-center"
      >
        No files match your search.
      </p>
      <p v-else class="text-gray-400 h-[65vh] flex justify-center items-center">
        <button @click="openFileUpload" class="text-prime-golden flex items-center gap-3">
          <img src="../assets/images/add-icon.svg" class="w-6 h-6" />
          Click to add a file!
        </button>
      </p>
    </div>

    <!-- Pagination Controls -->
    <div
      class="flex justify-center items-center py-4 bg-pitch-black-900 fixed bottom-0 w-full border-t border-gray-600"
    >
      <button
        @click="previousPage"
        :disabled="currentPage == 1"
        class="text-prime-golden px-2 disabled:text-prime-golden-dark"
      >
        <i class="fa fa-chevron-left"></i>
      </button>
      <span class="text-sm text-total-white mx-4">
        {{ currentPage }}/{{ totalPages }}
      </span>
      <button
        @click="nextPage"
        :disabled="!hasMoreFiles"
        class="text-prime-golden px-2 disabled:text-prime-golden-dark"
      >
        <i class="fa fa-chevron-right"></i>
      </button>
    </div>

    <!-- File Upload Dialog -->
    <input ref="fileInput" type="file" class="hidden" @change="handleFileUpload" />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useFileStore } from "@/stores/file";
import { useRoute } from "vue-router";
import FileCard from "@/components/FileCard.vue";
import { useContactsStore } from "@/stores/contact";
import { useLogStore } from "@/stores/log";
import { useRouter } from "vue-router";

const fileStore = useFileStore();
const contactStore = useContactsStore();
const logStore = useLogStore();
const router = useRouter();
const route = useRoute();
const projectId = computed(() => route.params.id);

const files = computed(() => {
  return fileStore.files.map(file => {
    const contact = contactStore.contacts.find(contact => contact.id === file.stakeholder_id);
    return {...file, uploader: contact ? contact.first_name : "Unknown"};
  });
});

const hasMoreFiles = computed(() => {
  return searchQuery.value.trim()
    ? currentPage.value < totalPages.value
    : fileStore.currentPage * fileStore.size < fileStore.totalFiles;
});

const searchQuery = ref("");
const isSearching = ref(false);
const error = ref(null);
const currentPage = ref(1);
const totalPages = ref(1);
const pageSize = 10;

onMounted(async () => {
  try {
    await fetchFiles();
    await contactStore.fetchContactsByProjectId(projectId.value);
  } catch (err) {
    error.value = "Failed to load files. Please try again later.";
    console.error("Error fetching files:", err);
  }
});

// Fetch files with pagination
const fetchFiles = async () => {
  try {
    await fileStore.fetchFilesByProjectId(projectId.value, currentPage.value);
    totalPages.value = Math.max(1, Math.ceil(fileStore.totalFiles / pageSize));
  } catch (err) {
    error.value = "Failed to load files. Please try again later.";
    console.error("Error fetching files:", err);
  }
};

// Delete a file
const deleteFile = async (fileId) => {
  await fileStore.deleteFile(fileId);
  await fetchFiles();
};

// Open file upload dialog
const openFileUpload = () => {
  document.querySelector('input[type="file"]').click();
};

// Handle file upload
const handleFileUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  try {
    await fileStore.uploadFile(file, projectId.value);
    await fetchFiles();
  } catch (err) {
    console.error("File upload failed:", err);
  }
};

// Toggle search bar visibility
const toggleSearch = () => {
  isSearching.value = !isSearching.value;
  if (!isSearching.value) {
    searchQuery.value = "";
    fetchFiles();
  }
};

// Perform search
const searchFiles = async () => {
  try {
    if (searchQuery.value.trim()) {
      await fileStore.searchFiles(projectId.value, searchQuery.value.trim());
    } else {
      await fetchFiles();
    }
  } catch (err) {
    error.value = "An error occurred while searching files.";
    console.error("Error during search:", err);
  }
};

// Pagination functions
const previousPage = async () => {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
    await fetchFiles();
  }
};

const nextPage = async () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value += 1;
    await fetchFiles();
  }
};

// Filtered files based on search query
const filteredFiles = computed(() => {
  if (!searchQuery.value) return files.value;
  const query = searchQuery.value.toLowerCase();
  const filteredFiles = files.value.filter((file) => file.file_name.toLowerCase().includes(query));
  return filteredFiles.map(file => {
    const contact = contactStore.contacts.find(contact => contact.id === file.stakeholder_id);
    return {...file, uploader: contact ? contact.first_name : "Unknown"};
  });
});

// Go to thread
const goToThread = async (threadId) => {
  try {
    await logStore.fetchMessagesByThreadId(threadId);
    if (logStore.threadMessages[0]) {
      const commType = logStore.threadMessages[0].comm_type;
      if (commType == "call") {
        router.push({ name: "CallLogPage", params: { projectId: projectId.value, threadId: threadId, fromFiles: true } });
      } else if (commType == "sms") {
        router.push({ name: "TextPage", params: { projectId: projectId.value, threadId: threadId, fromFiles: true } });
      } else {
        router.push({ name: "EmailViewPage", params: { projectId: projectId.value, threadId: threadId, fromFiles: true } });
      }
    }
  } catch (err) {
    error.value = "An error occurred while fetching messages.";
    console.error("Error during fetching messages:", err);
  }
};
</script>

<style scoped>
  .cards-section-height {
    height: calc(100vh - 16rem);
  }
</style>
